import type { AiFilledMark, CompanyRef, Paged, Ref } from './types';

// The People and Companies lists: row shapes, filters, sorting, columns and saved views, shared by the server and the
// web app.

export type ListEntity = 'people' | 'companies';

/** When the team last spoke with someone. "Over" choices include records nobody has contacted yet. */
export type LastContact = 'within7' | 'within30' | 'over14' | 'over30' | 'over60' | 'over90' | 'never';

export const LAST_CONTACT_OPTIONS: { value: LastContact; label: string; short: string }[] = [
  { value: 'within7', label: 'In the last 7 days', short: 'Last 7 days' },
  { value: 'within30', label: 'In the last 30 days', short: 'Last 30 days' },
  { value: 'over14', label: 'More than 14 days ago', short: 'Over 14 days ago' },
  { value: 'over30', label: 'More than 30 days ago', short: 'Over 30 days ago' },
  { value: 'over60', label: 'More than 60 days ago', short: 'Over 60 days ago' },
  { value: 'over90', label: 'More than 90 days ago', short: 'Over 90 days ago' },
  { value: 'never', label: 'Never contacted', short: 'Never' },
];

export type DealsFilter = 'open' | 'won' | 'none';

export const DEALS_OPTIONS: { value: DealsFilter; label: string }[] = [
  { value: 'open', label: 'Has an open deal' },
  { value: 'won', label: 'Has a won deal' },
  { value: 'none', label: 'No deals yet' },
];

/** A filter on one custom field. Which parts apply depends on the field type. */
export interface CustomFilter {
  /** Select, multiselect and checkbox ("yes" / "no"): any of these values. */
  values?: string[];
  /** Text and link fields: contains this text. */
  text?: string;
  /** Number fields: at least / at most. Date fields: on or after / on or before (YYYY-MM-DD). */
  min?: number | string | null;
  max?: number | string | null;
  /** Only records where the field is empty. */
  empty?: boolean;
}

export interface ListFilters {
  /** Teammate ids, "me" for whoever is looking, "none" for records with no owner. */
  owner?: string[];
  /** Any of these tags (ignoring case). */
  tags?: string[];
  /** People only: any of these companies, or "none" for people without one. */
  company?: string[];
  source?: string[];
  /** Companies only. */
  industry?: string[];
  /** Companies only: any of these markets. */
  market?: string[];
  /** Companies only: strong at any of these capabilities. */
  strengths?: string[];
  /** Companies only: lacking any of these capabilities. */
  lacks?: string[];
  /** Companies only: lacking something the team offers. */
  fit?: boolean;
  last?: LastContact;
  deals?: DealsFilter;
  custom?: Record<string, CustomFilter>;
}

export type SortDir = 'asc' | 'desc';

export interface ListSort {
  /** A column key, e.g. "name", "last" or "cf:language". */
  key: string;
  dir: SortDir;
}

export const PAGE_SIZE = 100;
/** The most rows "select all matching" picks up in one go. */
export const SELECT_ALL_LIMIT = 5000;

export interface PersonRow {
  id: string;
  name: string;
  hue: number;
  title: string | null;
  company: CompanyRef | null;
  /** The primary address first. */
  emails: string[];
  phone: string | null;
  location: string | null;
  ownerId: string | null;
  tags: string[];
  source: string | null;
  sourceDetail: string | null;
  lastContactedAt: string | null;
  createdAt: string;
  custom: Record<string, unknown>;
  openDeals: number;
  /** Sum of open deal values in the workspace's main currency. */
  openValue: number;
  doNotEmail: boolean;
  doNotEmailReason: string | null;
  unsubscribedAt: string | null;
  aiFilled: Record<string, AiFilledMark>;
  /** Another live person at the same company who looks like the same person. */
  duplicateOf: Ref | null;
}

export interface CompanyRow {
  id: string;
  name: string;
  hue: number;
  /** The logo from the company's website, as a logo id. */
  logo: string | null;
  domain: string | null;
  website: string | null;
  industry: string | null;
  staff: number | null;
  location: string | null;
  ownerId: string | null;
  tags: string[];
  source: string | null;
  sourceDetail: string | null;
  lastContactedAt: string | null;
  createdAt: string;
  custom: Record<string, unknown>;
  aiFilled: Record<string, AiFilledMark>;
  /** What they do, in a line. */
  summary: string | null;
  market: string | null;
  /** Who they sell to. */
  clients: string | null;
  strengths: string[];
  /** Capabilities they lack. */
  lacks: string[];
  /** The team's angle of approach. */
  angle: string | null;
  peopleCount: number;
  openDeals: number;
  /** Sum of open deal values in the workspace's main currency. */
  openValue: number;
  wonDeals: number;
  lostDeals: number;
  /** Another live company that looks like the same one. */
  duplicateOf: CompanyRef | null;
}

export type ListRow = PersonRow | CompanyRow;

export interface ListPage<T> extends Paged<T> {
  /** The currency of `openValue`. */
  currency: string;
}

export interface SavedView {
  id: string;
  entity: ListEntity;
  name: string;
  filters: ListFilters;
  sort: ListSort | null;
  /** Visible column keys in order, after the name. Empty means the default columns. */
  columns: string[];
  position: number;
  createdBy: Ref | null;
  createdAt: string;
  updatedAt: string;
}

/** Row counts per saved view, plus "all" for the whole list. */
export type ViewCounts = Record<string, number>;

export interface FacetValue {
  value: string;
  count: number;
}

export interface ListFacets {
  sources: FacetValue[];
  industries: FacetValue[];
  markets: FacetValue[];
}

export interface TagCount {
  tag: string;
  people: number;
  companies: number;
  deals: number;
}

/** Changes to many people or companies at once. */
export interface BulkRequest {
  ids: string[];
  addTags?: string[];
  removeTags?: string[];
  ownerId?: string | null;
  /** People only. */
  companyId?: string | null;
  /** Companies only: set every company's market, or clear it with null. */
  market?: string | null;
  /** Companies only: add a capability to their strengths or gaps (moving it off the other side), or remove it. */
  capability?: { side: 'strengths' | 'lacks'; name: string; mode: 'add' | 'remove' };
}

/** A company's strengths and gaps before and after a change, so it can be undone where nobody changed them since. */
export interface CapabilityChange {
  id: string;
  before: { strengths: string[]; lacks: string[] };
  after: { strengths: string[]; lacks: string[] };
}

export interface BulkResult {
  /** The records that actually changed. */
  ids: string[];
  /** Their values before the change, so it can be undone. Companies also carry their market. */
  before: { id: string; ownerId: string | null; companyId: string | null; tags: string[]; market?: string | null }[];
  /** For a capability change: each changed company's strengths and gaps before and after. */
  capabilities?: CapabilityChange[];
}

export type MergeType = 'person' | 'company';

export interface MergeRequest {
  type: MergeType;
  keepId: string;
  mergeIds: string[];
  /** For each field that differs, the id of the record whose value wins. Fields left out keep the main record's value, or the first filled-in one. */
  values: Record<string, string>;
}

/** What comes along with each record in a merge. */
export interface MergeCounts {
  emails: number;
  deals: number;
  notes: number;
  files: number;
  followups: number;
  people: number;
}

/** A person as the merge dialog compares them: the list row plus the fields the list leaves out. */
export interface MergePerson extends PersonRow {
  linkedin: string | null;
}

/** A company as the merge dialog compares them: the list row plus the fields the list leaves out. */
export interface MergeCompany extends CompanyRow {
  phone: string | null;
  founded: number | null;
  linkedin: string | null;
  description: string | null;
}

export interface MergeCandidates {
  records: (MergePerson | MergeCompany)[];
  counts: Record<string, MergeCounts>;
}

/* ---------- columns ---------- */

export interface ColumnDef {
  key: string;
  label: string;
  width: number;
  /** Right-aligned numbers. */
  num?: boolean;
  /** Has a sort. */
  sortable: boolean;
  /** Shown when a view has not chosen its columns. */
  byDefault: boolean;
}

export const PEOPLE_COLUMNS: ColumnDef[] = [
  { key: 'name', label: 'Name', width: 262, sortable: true, byDefault: true },
  { key: 'title', label: 'Title', width: 196, sortable: true, byDefault: true },
  { key: 'company', label: 'Company', width: 186, sortable: true, byDefault: true },
  { key: 'email', label: 'Email', width: 262, sortable: true, byDefault: true },
  { key: 'tags', label: 'Tags', width: 200, sortable: true, byDefault: true },
  { key: 'owner', label: 'Owner', width: 130, sortable: true, byDefault: true },
  { key: 'source', label: 'Source', width: 262, sortable: true, byDefault: true },
  { key: 'last', label: 'Last contacted', width: 136, sortable: true, byDefault: true },
  { key: 'phone', label: 'Phone', width: 160, sortable: true, byDefault: false },
  { key: 'location', label: 'Location', width: 160, sortable: true, byDefault: false },
  { key: 'deals', label: 'Open deals', width: 150, num: true, sortable: true, byDefault: false },
  { key: 'created', label: 'Added', width: 120, sortable: true, byDefault: false },
];

export const COMPANY_COLUMNS: ColumnDef[] = [
  { key: 'name', label: 'Company', width: 262, sortable: true, byDefault: true },
  { key: 'domain', label: 'Domain', width: 180, sortable: true, byDefault: true },
  { key: 'market', label: 'Market', width: 150, sortable: true, byDefault: true },
  { key: 'industry', label: 'Industry', width: 180, sortable: true, byDefault: true },
  { key: 'lacks', label: 'Gaps', width: 290, sortable: true, byDefault: true },
  { key: 'staff', label: 'Staff', width: 96, num: true, sortable: true, byDefault: true },
  { key: 'location', label: 'Location', width: 140, sortable: true, byDefault: true },
  { key: 'open', label: 'Open deals', width: 160, num: true, sortable: true, byDefault: true },
  { key: 'people', label: 'People', width: 84, num: true, sortable: true, byDefault: true },
  { key: 'owner', label: 'Owner', width: 130, sortable: true, byDefault: true },
  { key: 'source', label: 'Source', width: 290, sortable: true, byDefault: true },
  { key: 'fit', label: 'Gaps you fill', width: 140, num: true, sortable: true, byDefault: false },
  { key: 'strengths', label: 'Strengths', width: 220, sortable: true, byDefault: false },
  { key: 'summary', label: 'What they do', width: 280, sortable: true, byDefault: false },
  { key: 'clients', label: 'Sells to', width: 220, sortable: true, byDefault: false },
  { key: 'angle', label: 'Our angle', width: 280, sortable: true, byDefault: false },
  { key: 'tags', label: 'Tags', width: 200, sortable: true, byDefault: false },
  { key: 'last', label: 'Last contacted', width: 136, sortable: true, byDefault: false },
  { key: 'created', label: 'Added', width: 120, sortable: true, byDefault: false },
];

export function baseColumns(entity: ListEntity): ColumnDef[] {
  return entity === 'people' ? PEOPLE_COLUMNS : COMPANY_COLUMNS;
}

/** The key of a custom field's column. */
export function customColumn(fieldKey: string): string {
  return `cf:${fieldKey}`;
}

/** The custom field key behind a column key, or null for a built-in column. */
export function customKeyOf(column: string): string | null {
  return column.startsWith('cf:') ? column.slice(3) : null;
}

/** The columns a list shows when its view has not chosen any: the built-in defaults, then the first custom field. */
export function defaultColumns(entity: ListEntity, customKeys: string[]): string[] {
  const cols = baseColumns(entity)
    .filter((c) => c.byDefault && c.key !== 'name')
    .map((c) => c.key);
  if (customKeys[0]) cols.push(customColumn(customKeys[0]));
  return cols;
}

/** Visible columns after dropping unknown ones (a removed custom field) and duplicates. Name always comes first. */
export function cleanColumns(entity: ListEntity, columns: string[] | null | undefined, customKeys: string[]): string[] {
  if (!columns || columns.length === 0) return defaultColumns(entity, customKeys);
  const known = new Set([...baseColumns(entity).map((c) => c.key), ...customKeys.map(customColumn)]);
  const seen = new Set<string>();
  const out: string[] = [];
  for (const c of columns) {
    if (c === 'name' || !known.has(c) || seen.has(c)) continue;
    seen.add(c);
    out.push(c);
  }
  return out;
}

/** True when the filters narrow the list at all. */
export function hasFilters(f: ListFilters | null | undefined): boolean {
  if (!f) return false;
  return (
    !!f.owner?.length ||
    !!f.tags?.length ||
    !!f.company?.length ||
    !!f.source?.length ||
    !!f.industry?.length ||
    !!f.market?.length ||
    !!f.strengths?.length ||
    !!f.lacks?.length ||
    !!f.fit ||
    !!f.last ||
    !!f.deals ||
    Object.values(f.custom ?? {}).some(customFilterActive)
  );
}

export function customFilterActive(c: CustomFilter | undefined): boolean {
  if (!c) return false;
  return !!c.empty || !!c.values?.length || !!c.text?.trim() || (c.min !== undefined && c.min !== null && c.min !== '') || (c.max !== undefined && c.max !== null && c.max !== '');
}

/** Drops empty parts so two equal filters compare equal. */
export function compactFilters(f: ListFilters | null | undefined): ListFilters {
  const out: ListFilters = {};
  if (!f) return out;
  if (f.owner?.length) out.owner = [...f.owner];
  if (f.tags?.length) out.tags = [...f.tags];
  if (f.company?.length) out.company = [...f.company];
  if (f.source?.length) out.source = [...f.source];
  if (f.industry?.length) out.industry = [...f.industry];
  if (f.market?.length) out.market = [...f.market];
  if (f.strengths?.length) out.strengths = [...f.strengths];
  if (f.lacks?.length) out.lacks = [...f.lacks];
  if (f.fit) out.fit = true;
  if (f.last) out.last = f.last;
  if (f.deals) out.deals = f.deals;
  const custom: Record<string, CustomFilter> = {};
  for (const [k, v] of Object.entries(f.custom ?? {})) {
    if (!customFilterActive(v)) continue;
    const c: CustomFilter = {};
    if (v.empty) c.empty = true;
    else {
      if (v.values?.length) c.values = [...v.values];
      if (v.text?.trim()) c.text = v.text.trim();
      if (v.min !== undefined && v.min !== null && v.min !== '') c.min = v.min;
      if (v.max !== undefined && v.max !== null && v.max !== '') c.max = v.max;
    }
    custom[k] = c;
  }
  if (Object.keys(custom).length) out.custom = custom;
  return out;
}

/** A stable text form of filters, for comparing and for query keys. */
export function filtersKey(f: ListFilters | null | undefined): string {
  const c = compactFilters(f);
  const sorted = (v: unknown): unknown => {
    if (Array.isArray(v)) return v.map(sorted);
    if (v && typeof v === 'object') {
      return Object.fromEntries(
        Object.keys(v as Record<string, unknown>)
          .sort()
          .map((k) => [k, sorted((v as Record<string, unknown>)[k])]),
      );
    }
    return v;
  };
  return JSON.stringify(sorted(c));
}

/**
 * Filters after a choice field's choices changed: renamed choices ({ old: new }) follow their new name, choices no
 * longer listed drop out, and a filter left without choices goes away.
 */
export function filtersAfterChoices(f: ListFilters, key: string, renames: Record<string, string>, options: string[]): ListFilters {
  const c = f.custom?.[key];
  if (!c?.values?.length) return f;
  const renamed = new Map(Object.entries(renames));
  const listed = new Set(options);
  const values = [...new Set(c.values.map((v) => renamed.get(v) ?? v))].filter((v) => listed.has(v));
  return compactFilters({ ...f, custom: { ...f.custom, [key]: { ...c, values } } });
}

/** A list address's filters (its `f` parameter, as JSON) after a choice field's choices changed. */
export function filtersParamAfterChoices(param: string, key: string, renames: Record<string, string>, options: string[]): string {
  let f: unknown;
  try {
    f = JSON.parse(param);
  } catch {
    return param;
  }
  if (!f || typeof f !== 'object' || Array.isArray(f)) return param;
  const next = filtersAfterChoices(f as ListFilters, key, renames, options);
  return next === f ? param : JSON.stringify(next);
}

/**
 * Filters after a capability or market was renamed (`to`) or removed (`to` null) across the workspace: the old
 * name follows its new one, or drops out.
 */
export function filtersAfterTerm(f: ListFilters, kind: 'capability' | 'market', from: string, to: string | null): ListFilters {
  const swap = (list: string[] | undefined) => {
    if (!list?.some((v) => v.toLowerCase() === from.toLowerCase())) return list;
    const next = list.flatMap((v) => (v.toLowerCase() === from.toLowerCase() ? (to ? [to] : []) : [v]));
    return [...new Map(next.map((v) => [v.toLowerCase(), v])).values()];
  };
  const next: ListFilters = kind === 'market' ? { ...f, market: swap(f.market) } : { ...f, strengths: swap(f.strengths), lacks: swap(f.lacks) };
  return compactFilters(next);
}

/** A list address's filters (its `f` parameter, as JSON) after a capability or market was renamed or removed. */
export function filtersParamAfterTerm(param: string, kind: 'capability' | 'market', from: string, to: string | null): string {
  let f: unknown;
  try {
    f = JSON.parse(param);
  } catch {
    return param;
  }
  if (!f || typeof f !== 'object' || Array.isArray(f)) return param;
  const before = compactFilters(f as ListFilters);
  const after = filtersAfterTerm(before, kind, from, to);
  return filtersKey(before) === filtersKey(after) ? param : JSON.stringify(after);
}
